import React, { useEffect, useState } from 'react';

import { TransactionDirection, TransactionProject } from 'api-client';
import { dateRangeMapped, DateRangeType, transformToDates } from 'api-client/enums/DateRangeType';
import download from 'assets/images/svg/download.svg';
import { Button, Image, Loader, TertiaryButton } from 'components/common';
import { TertiaryButtonColor } from 'components/common/buttons/decorators/TertiaryButton/TertiaryButton';
import { FilterBar } from 'components/common/filters';
import apiClientConfig from 'configs/app';
import { createTransactionProvider } from 'di';
import { useToggle } from 'hooks';
import moment from 'moment';
import { cn, Nullable } from 'utils';

import IdentificationDateSelect from '../../identification/IdentificationDateSelect';
import TransactionsProjectSelect from '../TransactionsProjectSelect';
import styles from './TransactionsPartialFilter.module.scss';

export type Filter = {
  search: string;
  dateFrom: string;
  dateTo: string;
  transactionDirection: Nullable<TransactionDirection>;
  projectUuids: Array<string>;
};

type Props = {
  filter: Filter;
  onChange: (filter: Filter) => void;
  projects: Array<TransactionProject>;
};

const ALL_PROJECTS_UUID = '-';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';

const directionPills: Array<{ value: Nullable<TransactionDirection>; label: string }> = [
  { value: null, label: 'Все транзакции' },
  { value: TransactionDirection.INCOME, label: 'Входящие' },
  { value: TransactionDirection.OUTGOING, label: 'Исходящие' },
];

const periodPills = Object.entries(dateRangeMapped).map(([type, label]) => ({
  type: type as DateRangeType,
  label,
}));

const toIsoString = (date: Nullable<Date>): string =>
  date ? moment(date).format(DATE_FORMAT) : '';

const TransactionsPartialFilter = ({ filter, onChange, projects }: Props) => {
  const [filterActive, toggleFilter] = useToggle();
  const [exportLoading, setExportLoading] = useState(false);

  const [draftProjects, setDraftProjects] = useState<Array<TransactionProject>>([]);
  const [period, setPeriod] = useState<Nullable<DateRangeType>>(null);
  const [dateFrom, setDateFrom] = useState<Nullable<Date>>(null);
  const [dateTo, setDateTo] = useState<Nullable<Date>>(null);

  useEffect(() => {
    if (!filterActive) return;
    setDraftProjects(projects.filter((project) => filter.projectUuids.includes(project.uuid)));
    setDateFrom(filter.dateFrom ? new Date(filter.dateFrom) : null);
    setDateTo(filter.dateTo ? new Date(filter.dateTo) : null);
  }, [filterActive]);

  const handleDirection = (direction: Nullable<TransactionDirection>): void => {
    onChange({ ...filter, transactionDirection: direction });
  };

  const handlePeriod = (type: DateRangeType): void => {
    if (period === type || type === DateRangeType.ALL) {
      setPeriod(type === DateRangeType.ALL ? type : null);
      setDateFrom(null);
      setDateTo(null);
      return;
    }
    const [to, from] = transformToDates(type);
    setPeriod(type);
    setDateFrom(from ?? null);
    setDateTo(to ?? null);
  };

  const handleCustomDate = (setter: (date: Date) => void) => (date: Date) => {
    setPeriod(null);
    setter(date);
  };

  const handleApply = (): void => {
    const hasAllProjects = draftProjects.some((project) => project.uuid === ALL_PROJECTS_UUID);
    onChange({
      ...filter,
      projectUuids: hasAllProjects ? [] : draftProjects.map((project) => project.uuid),
      dateFrom: toIsoString(dateFrom),
      dateTo: toIsoString(dateTo),
    });
    toggleFilter();
  };

  const handleReset = (): void => {
    setDraftProjects([]);
    setPeriod(null);
    setDateFrom(null);
    setDateTo(null);
    onChange({ ...filter, projectUuids: [], dateFrom: '', dateTo: '' });
  };

  const exportStatement = async (): Promise<void> => {
    setExportLoading(true);
    try {
      const provider = await createTransactionProvider();
      const response = await provider.exportStatement(filter);
      window.location.assign(apiClientConfig.apiBaseUrl + response.url);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <FilterBar>
        <FilterBar.Pills>
          {directionPills.map((pill) => {
            const isActive = filter.transactionDirection === pill.value;
            return (
              <FilterBar.Pill
                key={pill.label}
                active={isActive}
                showClear={isActive && pill.value !== null}
                onClick={() => handleDirection(pill.value)}
                onClear={() => handleDirection(null)}
              >
                {pill.label}
              </FilterBar.Pill>
            );
          })}
        </FilterBar.Pills>
        <FilterBar.Actions>
          <button
            type="button"
            className={styles.download}
            onClick={exportStatement}
            aria-label="Скачать выписку"
          >
            {exportLoading ? (
              <Loader size="small" overlay={false} />
            ) : (
              <Image src={download} alt="" className={styles.downloadIcon} />
            )}
          </button>
          <FilterBar.Toggle
            expanded={filterActive}
            onClick={toggleFilter}
            label={filterActive ? 'Скрыть фильтры' : 'Показать фильтры'}
          />
        </FilterBar.Actions>
      </FilterBar>

      <div className={cn(styles.panel, filterActive && styles.panelActive)}>
        <div className={styles.group}>
          <div className={styles.groupTitle}>Проект</div>
          <TransactionsProjectSelect
            projects={projects}
            selectedProjects={draftProjects}
            onSelected={setDraftProjects}
          />
        </div>

        <div className={styles.group}>
          <div className={styles.groupTitle}>Период</div>
          <div className={styles.periods}>
            {periodPills.map((pill) => (
              <FilterBar.Pill
                key={pill.type}
                active={period === pill.type}
                onClick={() => handlePeriod(pill.type)}
              >
                {pill.label}
              </FilterBar.Pill>
            ))}
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.groupTitle}>Свой период</div>
          <div className={styles.dates}>
            <IdentificationDateSelect
              value={dateFrom}
              onSelected={handleCustomDate(setDateFrom)}
              header="с"
              direction="row"
            />
            <IdentificationDateSelect
              value={dateTo}
              onSelected={handleCustomDate(setDateTo)}
              header="по"
              direction="row"
            />
          </div>
        </div>

        <div className={styles.submit}>
          <TertiaryButton color={TertiaryButtonColor.GREEN}>
            <Button onClick={handleApply}>Применить фильтр</Button>
          </TertiaryButton>
          <TertiaryButton color={TertiaryButtonColor.WHITE_GREEN}>
            <Button onClick={handleReset}>Сбросить</Button>
          </TertiaryButton>
        </div>
      </div>
    </>
  );
};

export default TransactionsPartialFilter;
